import type React from 'react'
import { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon } from 'lucide-react'

import type { InputGradeCode } from '@/api/model/userType'

interface GradeStepProps {
  value: string
  onChange: (grade: InputGradeCode | null) => void
  birthDate: string
}

const gradeOptions = [
  { value: '', label: '선택하세요', disabled: true },
  // { value: 'g1', label: '초등학교 1학년' },
  // { value: 'g2', label: '초등학교 2학년' },
  // { value: 'g3', label: '초등학교 3학년' },
  { value: 'g17', label: '기타' },
  { value: 'g16', label: '검정' },
  { value: 'g15', label: '재수' },
  // { value: 'g14', label: '(중)검정' },
  // { value: 'g13', label: '(중)재수' },
  { value: 'g12', label: '고등학교 3학년' },
  { value: 'g11', label: '고등학교 2학년' },
  { value: 'g10', label: '고등학교 1학년' },
  { value: 'g9', label: '중학교 3학년' },
  { value: 'g8', label: '중학교 2학년' },
  { value: 'g7', label: '중학교 1학년' },
  { value: 'g6', label: '초등학교 6학년' },
  { value: 'g5', label: '초등학교 5학년' },
  { value: 'g4', label: '초등학교 4학년' }
]

export function GradeStep({ value, onChange, birthDate }: GradeStepProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<HTMLDivElement>(null)

  // 생일에 따른 메시지 생성
  const getGradeMessage = () => {
    if (!birthDate) {
      return '학년도 알려줄 수 있어?'
    }

    const today = new Date()
    const birth = new Date(birthDate)

    // 올해 생일 날짜 계산
    const thisYearBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate())

    // 오늘이 생일인지 확인
    const isToday = today.toDateString() === thisYearBirthday.toDateString()

    if (isToday) {
      return '오늘이라구?! 정말 축하해!\n그럼 학년도 알려줄 수 있어?'
    }

    // 한달 이내인지 확인
    const oneMonthFromNow = new Date(today)
    oneMonthFromNow.setMonth(today.getMonth() + 1)

    const isWithinOneMonth = thisYearBirthday >= today && thisYearBirthday <= oneMonthFromNow

    if (isWithinOneMonth) {
      return '얼마 안남았구나!\n학년도 알려줄 수 있어?'
    }

    return '벌써 생일이 기다려지네!\n학년도 알려줄 수 있어?'
  }

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 키보드 이벤트 처리
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (selectedIndex >= 0 && !gradeOptions[selectedIndex].disabled) {
        selectOption(gradeOptions[selectedIndex])
      } else {
        setIsOpen(!isOpen)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (isOpen) {
        const nextIndex = selectedIndex < gradeOptions.length - 1 ? selectedIndex + 1 : 0
        setSelectedIndex(nextIndex)
      } else {
        setIsOpen(true)
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (isOpen) {
        const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : gradeOptions.length - 1
        setSelectedIndex(prevIndex)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setSelectedIndex(-1)
    }
  }

  // 옵션 선택
  const selectOption = (option: (typeof gradeOptions)[0]) => {
    if (!option.disabled) {
      onChange(option.value as InputGradeCode | null)
      setIsOpen(false)
      setSelectedIndex(-1)
    }
  }

  // 현재 선택된 옵션 찾기
  const selectedOption = gradeOptions.find(option => option.value === value)

  return (
    <div className='space-y-4'>
      <div className='space-y-2 mb-6 text-center'>
        <h2 className='text-[24px] font-[700] text-[#020202]'>
          {getGradeMessage()
            .split('\n')
            .map((line, index) => (
              <span key={index}>
                {line}
                {index < getGradeMessage().split('\n').length - 1 && <br />}
              </span>
            ))}
        </h2>
      </div>
      <div className='relative'>
        <div
          ref={selectRef}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          className='w-full px-6 py-3 rounded-2xl bg-white/70 backdrop-blur-sm border-2 border-[#FFA236]/20 focus:border-[#FFA236] focus:outline-none focus:ring-2 focus:ring-[#FFA236]/50 transition-all duration-300 text-center text-[18px] font-[500] cursor-pointer flex items-center justify-between'
        >
          <span className={`flex-1 ${!selectedOption || selectedOption.disabled ? 'text-gray-400' : 'text-gray-800'}`}>
            {selectedOption ? selectedOption.label : '선택하세요'}
          </span>
          <ChevronDownIcon
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>

        {/* 드롭다운 리스트 */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto'
          >
            {gradeOptions.slice(1).map((option, index) => (
              <div
                key={option.value}
                onClick={() => selectOption(option)}
                className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                  selectedIndex === index + 1 ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                } ${option.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <div className='font-medium text-center'>{option.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
